import { useState, useRef } from "react";
import { Add20Regular, Dismiss20Regular, Document20Regular } from "@fluentui/react-icons";
import { uploadTechnicalDocument } from "../technicalDocuments/technicalDocumentsApi";

interface TechnicalDocumentUploadProps {
  budgetId: number;
  onUploaded: () => void;
  onQueueChanged?: (hasFiles: boolean) => void;
}

interface QueuedFile {
  file: File;
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
}

export default function TechnicalDocumentUpload({
  budgetId,
  onUploaded,
  onQueueChanged,
}: TechnicalDocumentUploadProps) {
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    const pdfFiles = files.filter((f) => {
      const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
      return ext === ".pdf";
    });

    const rejected = files.length - pdfFiles.length;
    if (rejected > 0) {
      setGlobalError(`${rejected} ficheiro(s) ignorado(s) — apenas PDF é aceite.`);
    } else {
      setGlobalError(null);
    }

    if (pdfFiles.length === 0) return;

    setQueue((prev) => {
      const next = [
        ...prev,
        ...pdfFiles.map((f) => ({ file: f, status: "pending" as const })),
      ];
      onQueueChanged?.(next.length > 0);
      return next;
    });
  };

  const removeFile = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadAll = async () => {
    const pending = queue.filter((q) => q.status === "pending");
    if (pending.length === 0) return;

    setIsProcessing(true);
    setGlobalError(null);

    for (let i = 0; i < queue.length; i++) {
      if (queue[i].status !== "pending") continue;

      setQueue((prev) =>
        prev.map((q, idx) => (idx === i ? { ...q, status: "uploading" } : q)),
      );

      try {
        await uploadTechnicalDocument(budgetId, queue[i].file, "technical");
        setQueue((prev) =>
          prev.map((q, idx) => (idx === i ? { ...q, status: "done" } : q)),
        );
        onUploaded();
      } catch {
        setQueue((prev) =>
          prev.map((q, idx) =>
            idx === i ? { ...q, status: "error", error: "Erro ao carregar" } : q,
          ),
        );
      }
    }

    setIsProcessing(false);
  };

  const clearDone = () => {
    setQueue((prev) => {
      const next = prev.filter((q) => q.status !== "done");
      onQueueChanged?.(next.length > 0);
      return next;
    });
  };

  const hasPending = queue.some((q) => q.status === "pending");
  const allDone = queue.length > 0 && queue.every((q) => q.status === "done" || q.status === "error");
  const uploadingIndex = queue.findIndex((q) => q.status === "uploading");
  const uploadProgress = uploadingIndex >= 0
    ? `A carregar ${uploadingIndex + 1}/${queue.length}...`
    : null;

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Upload button */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf"
          multiple
          onChange={handleFilesSelected}
        />
        <button
          className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 disabled:opacity-60"
          style={{ borderRadius: "var(--radius-button)" }}
          onClick={() => fileRef.current?.click()}
          disabled={isProcessing}
        >
          <Add20Regular className="w-5 h-5" />
          Adicionar PDFs
        </button>

        {hasPending && !isProcessing && (
          <button
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground border border-primary rounded transition-colors flex items-center gap-2"
            style={{ borderRadius: "var(--radius-button)" }}
            onClick={handleUploadAll}
          >
            Carregar {queue.filter((q) => q.status === "pending").length} ficheiro(s)
          </button>
        )}

        {isProcessing && (
          <span className="text-sm text-secondary-text animate-pulse">
            {uploadProgress || "A processar..."}
          </span>
        )}

        {allDone && (
          <button
            className="text-sm text-secondary-text hover:text-foreground underline"
            onClick={clearDone}
          >
            Limpar lista
          </button>
        )}

        {globalError && (
          <span className="text-destructive text-sm">{globalError}</span>
        )}
      </div>

      {/* File list */}
      {queue.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {queue.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded"
              style={{ borderRadius: "var(--radius)" }}
            >
              <Document20Regular className="w-4 h-4 text-primary flex-shrink-0" />
              <span
                className="flex-1 text-sm text-foreground truncate"
                style={{ fontSize: "var(--text-sm)" }}
              >
                {item.file.name}
              </span>
              <span
                className={`text-xs flex-shrink-0 ${
                  item.status === "done"
                    ? "text-green-600"
                    : item.status === "error"
                      ? "text-destructive"
                      : item.status === "uploading"
                        ? "text-primary animate-pulse"
                        : "text-secondary-text"
                }`}
              >
                {item.status === "done"
                  ? "✓ Carregado"
                  : item.status === "error"
                    ? `✗ ${item.error || "Erro"}`
                    : item.status === "uploading"
                      ? "A carregar..."
                      : "Pendente"}
              </span>
              {item.status === "pending" && !isProcessing && (
                <button
                  onClick={() => removeFile(index)}
                  className="ml-1 text-secondary-text hover:text-foreground flex-shrink-0"
                >
                  <Dismiss20Regular className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
